import { useState, useRef } from "react";
import {
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { Cached, Download, CalendarToday } from "@mui/icons-material";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import MDBox from "@vendor/components/MDBox";
import MenuCard from "@vendor/examples/Cards/MenuCards";
import { ErrorAlert, InfoAlert } from "@shared/ui/alerts";
import { getFormatDate } from "@shared/utils/getFormatDate";
import { SelectController } from "@shared/ui/form";

import { AddDetail } from "./AddDetail";
import {
  generateInvoice,
  generateInvoiceByPeriod,
  confirmCurrentMonthPreInvoice,
} from "../lib/invoiceFlow";
import { buildPreInvoiceItemSchema } from "../schema/preInvoiceItemSchema";
import {
  previewPreInvoice,
  createPreInvoiceLastMonth,
  downloadPreInvoice,
  getCurrentPreInvoice,
  getPreInvoiceByEmailAndMonth,
  validatePreInvoiceHours,
  validatePreInvoiceLastMonth,
} from "../api/preInvoiceApi";
import { HTTP_STATUS, PREINVOICE_ENDPOINT, SWAL_COLOR } from "../constants";
import {
  filterMonthsByYear,
  getAvailableYearsAndMonths,
  isFutureMonthYear,
  monthRangeParams,
} from "../lib/preInvoiceRules";

const urlAddItem = PREINVOICE_ENDPOINT.ADD_ITEM;
const urlAddItemLastMonth = PREINVOICE_ENDPOINT.ADD_ITEM_LAST_MONTH;
const urlPreInvoicePending = PREINVOICE_ENDPOINT.PENDING;
const urlConfirmPreInvoice = PREINVOICE_ENDPOINT.CONFIRM;
const urlSendInvoice = PREINVOICE_ENDPOINT.SEND;

import type { PreInvoice, PreInvoiceDetail, UserProfile, ValidateHours } from "../api/preInvoiceApi";

export interface PreInvoiceItemProps {
  thisLoading: () => void;
  notLoading: () => void;
  getListPreInvoice: () => void;
  userData?: UserProfile;
}

export const PreInvoiceItem = ({ thisLoading, notLoading, getListPreInvoice, userData }: PreInvoiceItemProps) => {
  const { t } = useTranslation();
  const [addItemInvoiceDialog, setAddItemInvoiceDialog] = useState(false);
  const [urlAddItems, setUrlAddItems] = useState(urlAddItem);
  const onActiveAddItemInvoiceDialog = () => setAddItemInvoiceDialog(true);
  const onDisabledAddItemInvoiceDialog = () => setAddItemInvoiceDialog(false);
  const [openDatePicker, setOpenDatePicker] = useState(false);
  // Flujo del mes actual (Camino A): el resumen sale del preview (sin persistir) y los
  // conceptos adicionales se juntan en un ref (evita closures viejos en los diálogos Swal).
  const [conceptDialogOpen, setConceptDialogOpen] = useState(false);
  const previewRef = useRef<PreInvoice | null>(null);
  const additionalItemsRef = useRef<PreInvoiceDetail[]>([]);

  const months = [
    t("months.january"),
    t("months.february"),
    t("months.march"),
    t("months.april"),
    t("months.may"),
    t("months.june"),
    t("months.july"),
    t("months.august"),
    t("months.september"),
    t("months.october"),
    t("months.november"),
    t("months.december"),
  ];
  const { availableMonths, years, defaultMonth, defaultYear } = getAvailableYearsAndMonths(months);

  const getAvailableMonths = (selectedYear: string) => filterMonthsByYear(availableMonths, selectedYear);

  const { control, handleSubmit, watch, setValue } = useForm({
    resolver: yupResolver(buildPreInvoiceItemSchema({ t, months, years })),
    defaultValues: {
      month: defaultMonth,
      year: defaultYear,
    },
  });

  // Muestra el resumen del mes actual a partir del preview + los conceptos ya cargados.
  const showCurrentMonthSummary = () => {
    confirmCurrentMonthPreInvoice(
      previewRef.current as PreInvoice,
      additionalItemsRef.current,
      thisLoading,
      notLoading,
      {
        onAddConcept: () => setConceptDialogOpen(true),
        onInvoiceGenerated: getListPreInvoice,
      }
    );
  };

  // Pide el preview (NO persiste) y abre el resumen. La prefactura se crea recién al confirmar.
  const startCurrentMonthPreview = () => {
    additionalItemsRef.current = [];
    thisLoading();
    previewPreInvoice()
      .then((preview) => {
        notLoading();
        previewRef.current = preview;
        showCurrentMonthSummary();
      })
      .catch((error) => {
        const data = error.response?.data;
        notLoading();
        if (data?.code === HTTP_STATUS.BAD_REQUEST) {
          ErrorAlert(t("errorToGeneratePreInvoice"), data?.message ?? t("internalError")).then();
          return;
        }
        ErrorAlert("Error", t("errorToGeneratePreInvoice")).then();
      });
  };

  // Los conceptos adicionales del formulario se acumulan en el ref y se reabre el resumen.
  const handleConceptsCollected = (items: PreInvoiceDetail[]) => {
    additionalItemsRef.current = [...additionalItemsRef.current, ...items];
    setConceptDialogOpen(false);
    showCurrentMonthSummary();
  };

  const generatePreInvoiceLastMonth = () => {
    thisLoading();
    setUrlAddItems(urlAddItemLastMonth);
    createPreInvoiceLastMonth()
      .then((data: PreInvoice) => {
        getListPreInvoice();
        notLoading();
        generateInvoice(
          data,
          thisLoading,
          notLoading,
          onActiveAddItemInvoiceDialog,
          urlPreInvoicePending,
          urlConfirmPreInvoice,
          urlSendInvoice,
          getListPreInvoice
        );
      })
      .catch((error) => {
        const data = error.response?.data;
        notLoading();
        if (data?.code === HTTP_STATUS.BAD_REQUEST) {
          ErrorAlert(t("errorToGeneratePreInvoice"), data?.message ?? t("internalError")).then();
          return;
        }
        ErrorAlert("Error", t("errorToDownloadPreInvoice")).then();
      });
  };

  const generatePreInvoiceByMonthAndYear = (month: number, year: number) => {
    thisLoading();
    getPreInvoiceByEmailAndMonth(userData?.companyEmail as string, month, year)
      .then((data: PreInvoice) => {
        getListPreInvoice();
        notLoading();
        generateInvoiceByPeriod(
          data,
          thisLoading,
          notLoading,
          onActiveAddItemInvoiceDialog,
          urlPreInvoicePending,
          urlConfirmPreInvoice,
          urlSendInvoice,
          month,
          year,
          getListPreInvoice
        );
      })
      .catch((error) => {
        const data = error.response?.data;
        notLoading();
        if (data?.code === HTTP_STATUS.BAD_REQUEST) {
          ErrorAlert(t("errorToGeneratePreInvoice"), data?.message ?? t("internalError")).then();
          return;
        }
        ErrorAlert("Error", t("errorToDownloadPreInvoice")).then();
      });
  };

  // Valida las horas del mes actual. Si pasan (200), procede al preview. Si hay error de
  // validación (400), bloquea. Si faltan horas (100) NO bloquea: avisa y, si el colaborador
  // confirma, permite generar igual (ej.: baja a mitad de mes, mes en curso incompleto). El
  // backend no exige el mes completo para generar/enviar, así que la decisión queda en el usuario.
  const validateHour = () => {
    validatePreInvoiceHours()
      .then((data: ValidateHours) => {
        if (data.statusCode === HTTP_STATUS.SUCCESS) {
          startCurrentMonthPreview();
          return;
        }
        notLoading();
        if (data.statusCode === HTTP_STATUS.BAD_REQUEST) {
          ErrorAlert(data.message as string).then();
          return;
        }
        Swal.fire({
          title: t("incompleteHoursTitle"),
          html: `${data.message}<br><br>${t("generateWithIncompleteHoursQuestion")}`,
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: SWAL_COLOR.CONFIRM,
          cancelButtonColor: SWAL_COLOR.CANCEL,
          confirmButtonText: t("generateAnyway"),
          cancelButtonText: t("cancel"),
        }).then(({ isConfirmed }) => {
          if (isConfirmed) {
            startCurrentMonthPreview();
          }
        });
      })
      .catch(() => {
        notLoading();
        ErrorAlert("Error", t("errorToValidateLoadHour")).then();
      });
  };

  const generatePreInvoiceInitial = () => {
    thisLoading();
    validatePreInvoiceLastMonth()
      .then(async (data) => {
        notLoading();
        if (data.statusCode === HTTP_STATUS.SUCCESS) {
          Swal.fire({
            html: `${data.message} <br><br> ${t("generatePreInvoiceLastMonthQuestion")}`,
            title: t("preInvoiceLastMonthTitle"),
            icon: "info",
            showCancelButton: true,
            confirmButtonColor: SWAL_COLOR.CONFIRM,
            showDenyButton: true,
            cancelButtonColor: SWAL_COLOR.CANCEL,
            denyButtonColor: SWAL_COLOR.DENY,
            confirmButtonText: t("confirm"),
            denyButtonText: "No",
            cancelButtonText: t("cancel"),
          }).then(({ isConfirmed, isDenied }) => {
            if (isConfirmed) {
              generatePreInvoiceLastMonth();
            }
            if (isDenied) {
              validateHour();
            }
          });
        }
        if (data.statusCode === HTTP_STATUS.BAD_REQUEST) {
          validateHour();
        }
      })
      .catch((error) => {
        const data = error.response?.data;
        notLoading();
        ErrorAlert(t("errorTitle"), data?.message ?? t("internalError")).then();
      });
  };

  const onDownload = () => {
    thisLoading();
    getCurrentPreInvoice()
      .then(({ id }) => {
        downloadPreInvoice(id as number)
          .then(({ data, headers }) => {
            const link = document.createElement("a");
            link.target = "_blank";
            link.download = `${t("preInvoice")}_${userData?.firstName}_${userData?.lastName}${getFormatDate()}.pdf`;
            link.href = window.URL.createObjectURL(
              new Blob([data], {
                type: headers["content-type"] as string | undefined,
              })
            );
            link.click();
            notLoading();
          })
          .catch(() => notLoading());
      })
      .catch((error) => {
        const data = error.response?.data;
        notLoading();
        console.log(data);
        if (data?.code === HTTP_STATUS.BAD_REQUEST) {
          InfoAlert(t("errorToDownloadPreInvoice"), data?.message ?? t("internalError")).then();
          return;
        }
        ErrorAlert("Error", t("errorToGeneratePreInvoice")).then();
      });
  };

  const handleDateSelect = () => setOpenDatePicker(true);

  const validateMonthAndYear = ({ month, year }: { month: number; year: number }) => {
    if (isFutureMonthYear(month, year)) {
      ErrorAlert(t("errorTitle"), t("errorDateSelect"));
      return false;
    }
    return true;
  };

  const onSubmitPreInvoiceByMonthAndYear = (data: { month: string; year: string }) => {
    const month = months.indexOf(data.month) + 1;
    const year = parseInt(data.year);

    if (validateMonthAndYear({ month, year })) {
      generatePreInvoiceByMonthAndYear(month, year);
      setOpenDatePicker(false);
    }
  };

  const getMonthRangeText = () => t("feedbackMonthsAndYears", monthRangeParams(months));

  return (
    <>
      <Grid container spacing={3}>
        <Grid
          onClick={generatePreInvoiceInitial}
          size={{
            xs: 12,
            md: 6,
            lg: 4,
            xl: 4,
          }}
        >
          <MDBox mb={1.5}>
            <MenuCard icon={Cached} title={t("generate")} subtitle={t("generateSubtitle")} />
          </MDBox>
        </Grid>
        <Grid
          onClick={handleDateSelect}
          size={{
            xs: 12,
            md: 6,
            lg: 4,
            xl: 4,
          }}
        >
          <MDBox mb={1.5}>
            <MenuCard
              icon={CalendarToday}
              title={t("generateOtherMonth")}
              subtitle={t("generateOtherMonthSubtitle")}
            />
          </MDBox>
        </Grid>
        <Grid
          onClick={onDownload}
          size={{
            xs: 12,
            md: 6,
            lg: 4,
            xl: 4,
          }}
        >
          <MDBox mb={1.5}>
            <MenuCard icon={Download} title={t("download")} />
          </MDBox>
        </Grid>
        <Grid size={12}>
          <AddDetail
            value={addItemInvoiceDialog}
            onDisabled={onDisabledAddItemInvoiceDialog}
            thisLoading={thisLoading}
            notLoading={notLoading}
            onActive={onActiveAddItemInvoiceDialog}
            urlAddItem={urlAddItems}
            urlPreInvoicePending={urlPreInvoicePending}
            urlConfirmPreInvoice={urlConfirmPreInvoice}
            t={t}
          />
          {/* Modo colecta: para el flujo del mes actual, junta conceptos en estado sin postear. */}
          <AddDetail
            value={conceptDialogOpen}
            onDisabled={() => setConceptDialogOpen(false)}
            thisLoading={thisLoading}
            notLoading={notLoading}
            onCollect={handleConceptsCollected}
            t={t}
          />
        </Grid>
      </Grid>
      <Dialog
        open={openDatePicker}
        onClose={() => setOpenDatePicker(false)}
        slotProps={{
          paper: {
            sx: {
              width: "100%",
              maxWidth: "500px",
              borderRadius: "10px",
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: "1px solid",
            borderColor: "divider",
            pb: 2,
            px: 3,
            fontSize: "1.25rem",
            fontWeight: "medium",
          }}
        >
          {t("selectTitle")}
        </DialogTitle>
        <form onSubmit={handleSubmit(onSubmitPreInvoiceByMonthAndYear)}>
          <DialogContent
            sx={{
              py: 4,
              px: 3,
            }}
          >
            <MDBox
              mb={3}
              sx={{
                color: "text.secondary",
                fontSize: "0.875rem",
                lineHeight: 1.4,
              }}
            >
              {t("selectPeriodHint")}
            </MDBox>
            <MDBox
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: 2,
              }}
            >
              <SelectController
                control={control}
                name="month"
                label={t("monthLabel")}
                listValue={getAvailableMonths(watch("year")).months}
              />
              <SelectController
                control={control}
                name="year"
                label={t("yearLabel")}
                listValue={years}
                onChange={(e) => {
                  const selectedYear = e.target.value;
                  const { defaultMonth: nextDefaultMonth } = getAvailableMonths(selectedYear);
                  // `years` sale de `availableMonths`, asi que todo anio elegible tiene al menos un mes.
                  setValue("month", nextDefaultMonth!, { shouldValidate: true });
                }}
              />
            </MDBox>
            <MDBox
              mt={2}
              sx={{
                color: "text.secondary",
                fontSize: "0.875rem",
              }}
            >
              {getMonthRangeText()}
            </MDBox>
          </DialogContent>
          <DialogActions
            sx={{
              borderTop: "1px solid",
              borderColor: "divider",
              px: 3,
              py: 2,
              gap: 1,
            }}
          >
            <Button onClick={() => setOpenDatePicker(false)}>{t("cancel")}</Button>
            <Button type="submit" color="primary">
              {t("generate")}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
};
